/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState } from 'react';
import { debounce } from '@material-ui/core';
import { FormApi } from 'final-form';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import Input, { AccountInput, Select, SelectItem } from 'ui/Input';
import auth from 'utils/auth';
import { IOtherLookupRes } from 'components/modules/Transfers/tabs/BulkToAnotherBank.model';
import InputHint from '../InputHint/InputHint';
import { IInstantBank, IBank, IFormValues, IBranch, IApiBranchReponse } from '../Beneficiaries.model';

type AddBenFormType = FormApi<IFormValues, Partial<IFormValues>>;

interface IAddBeneficiaryOtherBank {
  values: IFormValues;
  form: AddBenFormType;
  instantBanks: IInstantBank[];
  nonInstantBanks: IBank[];
  setValidatedAccountNo: (value: boolean) => void;
  submitting: boolean;
  nameHint: string;
  setNameHint: (value: string) => void;
  setBeneficiaryId: (value: number) => void;
  isLookingUp: boolean;
  setLookingUp: (value: boolean) => void;
}

const AddBeneficiaryOtherBank = ({
  values,
  form,
  instantBanks,
  nonInstantBanks,
  setValidatedAccountNo,
  submitting,
  nameHint,
  setNameHint,
  setBeneficiaryId,
  isLookingUp,
  setLookingUp,
}: IAddBeneficiaryOtherBank) => {
  const [branchList, setBranchList] = useState<IBranch[]>([]);
  const [loadingBranchList, setLoadingBranchList] = useState(false);

  const searchBankBranch = (id: string) => {
    setLoadingBranchList(true);

    api
      .get(`/transfer/bankBranch?bankSortCode=${id}`, {})
      .then((data: IApiBranchReponse) => {
        setBranchList(data.banks);
        setValidatedAccountNo(true);
      })
      .catch((error: IApiResponse) => {
        console.error(error.description);
        setValidatedAccountNo(false);
      })
      .finally(() => {
        setLoadingBranchList(false);
      });
  };

  const lookupAccount = (bankCode: string, accountNumber: string) => {
    setLookingUp(true);
    api
      .post('transfer/otherbank/namelookup', {
        destinationAccount: accountNumber,
        sourceAccount: auth.data?.accountNumber,
        institutionId: bankCode,
      })
      .then((response: IOtherLookupRes) => {
        if (response.name) {
          setNameHint(response.name);
          setBeneficiaryId(response.beneficiaryId);
          setValidatedAccountNo(true);
        } else {
          setNameHint('Account not found');
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setNameHint(error.description);
      })
      .finally(() => setLookingUp(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeAccountNumber = ({ target }) => {
    if (values.bankCode && values.instantType === 'INSTANT') {
      setValidatedAccountNo(false);
      debounced(values.bankCode, target.value as string);
    } else {
      setValidatedAccountNo(true);
    }
  };

  const handleChangeInstantType = ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    form.mutators.changeFieldValue('bankCode', undefined);
    form.mutators.changeFieldValue('bankBranchCode', undefined);
    form.change('bankBranch', undefined);
    setValidatedAccountNo(false);
    if (target?.value === 'NON-INSTANT') {
      setNameHint('');
      setValidatedAccountNo(true);
    }
  };

  const handleChangeBank = ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    form.mutators.changeFieldValue('bankBranchCode', undefined);
    if (values.instantType === 'NON-INSTANT') {
      searchBankBranch(target?.value);
    }
  };

  const handleChangeBranch = ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    const selectedBranch = branchList.find((branch) => branch.BranchSortCode === target?.value);
    form.change('bankBranch', selectedBranch?.BranchName);
  };

  return (
    <>
      <Input name="fullName" label="Full Name" disabled={submitting} />
      <Select
        name="instantType"
        label="Transfer Type"
        disabled={submitting}
        SelectProps={{
          onChange: handleChangeInstantType,
        }}
      >
        <SelectItem key="instant" value="INSTANT">
          Instant Transfer
        </SelectItem>
        <SelectItem key="non-Instant" value="NON-INSTANT">
          Non-Instant Transfer
        </SelectItem>
      </Select>
      <Select
        name="bankCode"
        label="Bank"
        disabled={submitting || loadingBranchList}
        SelectProps={{
          onChange: handleChangeBank,
        }}
      >
        {values.instantType === 'INSTANT' && instantBanks.map(({ code, name }) => (
          <SelectItem key={code} value={code}>
            {name}
          </SelectItem>
        ))}
        {values.instantType === 'NON-INSTANT' && nonInstantBanks.map(({ BankName, BankSortCode }) => (
          <SelectItem key={BankName} value={BankSortCode}>
            {BankName}
          </SelectItem>
        ))}
      </Select>
      {/* {values.instantType === 'NON-INSTANT' && (
        <Select
          name="bankBranchCode"
          label="Bank Branch"
          disabled={submitting || loadingBranchList}
          SelectProps={{
            onChange: handleChangeBranch,
          }}
        >
          {branchList.map(({ BranchSortCode, BranchName }) => (
            <SelectItem key={BranchSortCode} value={BranchSortCode}>
              {BranchName}
            </SelectItem>
          ))}
        </Select>
      )} */}
      <AccountInput
        name="accountNumber"
        label="Account Number"
        disabled={submitting}
        onInput={handleChangeAccountNumber}
      />
      {(nameHint || isLookingUp) && <InputHint hint={nameHint} loading={isLookingUp} onClose={() => setNameHint('')} />}
    </>
  );
};

export default AddBeneficiaryOtherBank;
